import {
  NumberInput as BaseNumberInput,
  Box,
  Flex,
  InputGroup,
  InputRightAddon,
  NumberInputField,
} from '@chakra-ui/react'
import { getHeightFtIn } from '@hb/react'
import { HeightField } from '@hb/shared'
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { InputElement, InputProps } from '../../../../types/fields'
import { getInputContainerPaddingX, getInputContainerPaddingY } from '../Shared/InputContainer'
import { useStatusBorderColor } from '../Shared/hooks'
import { useInputSize } from '../Shared/sizes'
import { baseInputStyle } from '../Shared/styles'
const getHeightIn = (feet: number, inches: number) => feet * 12 + inches

export const HeightInput: InputElement<HeightField> = forwardRef<
  HTMLInputElement,
  InputProps<HeightField>
>((props, ref) => {
  const { input, field, disabled, readOnly, meta, inputStyle, style } = props
  const size = useInputSize(field?.size)
  const { optional, defaultValue } = field

  const { onChange, value } = input

  const borderColor = useStatusBorderColor(field, meta)

  const { feet, inches } = useMemo(() => getHeightFtIn(value ?? 0), [value])
  const feetRef = useRef<string>(feet ? feet.toString() : undefined)
  const inchesRef = useRef<string>(inches ? inches.toString() : undefined)
  const [feetString, setFeetString] = useState<string>(feetRef.current ?? '')
  const [inString, setInString] = useState<string>(inchesRef.current ?? '')

  useEffect(() => {
    if (feetRef.current !== undefined && inchesRef.current !== undefined) return
    if (!value) return
    const { feet, inches } = getHeightFtIn(value)
    feetRef.current = feet ? feet.toString() : undefined
    inchesRef.current = inches ? inches.toString() : undefined
    setInString(inchesRef.current ?? '')
    setFeetString(feetRef.current ?? '')
  }, [value])

  const handleChange = useCallback(() => {
    if (feetRef.current === undefined || isNaN(Number(feetRef.current))) return
    if (inchesRef.current === undefined || isNaN(Number(inchesRef.current))) return
    const newHeight = getHeightIn(Number(feetRef.current), Number(inchesRef.current))
    onChange({ target: { value: newHeight } })
  }, [onChange, feetRef, inchesRef])

  const defaultInputStyle = baseInputStyle(size, disabled)

  return (
    <Flex gap={3} alignItems="center">
      <Flex>
        <InputGroup
          height="auto"
          minH={0}
          border="0.5px solid"
          borderColor={borderColor}
          transition="border-color 0.15s ease"
          borderRadius="8px"
          width="auto"
          bg="white"
          alignItems="center">
          <BaseNumberInput
            {...input}
            isDisabled={disabled}
            isReadOnly={readOnly}
            onChange={asString => {
              feetRef.current = asString
              setFeetString(asString)
              handleChange()
              // onChange({ target: { value: field.precision ? asString : asNumber } })
            }}
            value={feetString}
            onFocus={e => {
              input.onFocus(e)
              e.target.select()
            }}
            onBlur={input.onBlur}
            lineHeight={1}
            onMouseUp={e => {
              e.preventDefault()
              // e.currentTarget.focus()
            }}
            min={0}
            h="auto"
            minH={0}
            max={12}
            isRequired={!optional}
            defaultValue={defaultValue}
            style={{ minHeight: 0, height: 'auto' }}
            step={1}
            precision={0}>
            <Box
              px={getInputContainerPaddingX(size)}
              py={getInputContainerPaddingY(size)}
              style={style}>
              <NumberInputField
                _focus={{
                  boxShadow: 'none',
                }}
                _placeholder={{ fontSize: '0.9em' }}
                ref={ref}
                outline="none"
                border="none"
                height="auto"
                minHeight={0}
                placeholder="Feet"
                borderColor="#cdcdcd"
                style={{ ...defaultInputStyle, ...inputStyle }}
              />
            </Box>
          </BaseNumberInput>
          <InputRightAddon
            fontSize={defaultInputStyle.fontSize}
            height="100%"
            borderRightWidth={0}
            borderTopWidth={0}
            borderBottomWidth={0}
            borderLeft="1px solid #cdcdcd">
            ft
          </InputRightAddon>
        </InputGroup>
      </Flex>
      <Flex>
        <InputGroup
          height="auto"
          minH={0}
          border="0.5px solid"
          borderColor={borderColor}
          transition="border-color 0.15s ease"
          borderRadius="8px"
          width="auto"
          bg="white"
          alignItems="center">
          <BaseNumberInput
            {...input}
            isDisabled={disabled}
            isReadOnly={readOnly}
            onChange={asString => {
              inchesRef.current = asString
              setInString(asString)
              handleChange()
              // onChange({ target: { value: field.precision ? asString : asNumber } })
            }}
            value={inString}
            min={0}
            max={11.9}
            h="auto"
            style={{ minHeight: 0, height: 'auto' }}
            isRequired={!optional}
            defaultValue={defaultValue}
            step={1}
            precision={1}
            lineHeight={1}>
            <Box
              px={getInputContainerPaddingX(size)}
              py={getInputContainerPaddingY(size)}
              style={style}>
              <NumberInputField
                _focus={{
                  boxShadow: 'none',
                }}
                _placeholder={{ fontSize: defaultInputStyle.fontSize }}
                outline="none"
                border="none"
                height="auto"
                minHeight={0}
                style={{ ...defaultInputStyle, ...inputStyle }}
                placeholder="Inches"
                borderColor="#cdcdcd"
              />
            </Box>
          </BaseNumberInput>
          <InputRightAddon
            fontSize={defaultInputStyle.fontSize}
            height="100%"
            borderRightWidth={0}
            borderTopWidth={0}
            borderBottomWidth={0}
            borderLeft="1px solid #cdcdcd">
            in
          </InputRightAddon>
        </InputGroup>
      </Flex>
    </Flex>
  )
})
